/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ProjectvariantSearchbar.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState, useEffect, useRef, memo } from "react";
import {
  Box,
  TextField,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  InputAdornment,
  Paper,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { HandleApiActions, projectConfigAPI } from "../../../../redux/actions";
import {
  selectProjectAction,
  selectVariantAction,
} from "../../../../redux/actions/projectConfigActions";

function ProjectVariantSearchbar() {
  const dispatch = useDispatch();
  const inputRef = useRef(null);

  // Redux values
  const projects = useSelector((s) => s?.projectConfigReducer?.projects) || [];
  const variantsFromRedux =
    useSelector((s) => s?.projectConfigReducer?.variants) || [];
  const selectedProjectDetails = useSelector(
    (s) => s?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariantDetails = useSelector(
    (s) => s?.projectConfigReducer?.selectedVariantDetails,
  );
  const selectedProjectId = useSelector(
    (s) => s?.projectConfigReducer?.selectedProjectId,
  );

  // Local state
  const [projectsData, setProjectsData] = useState([]); // [{ project_id, name, value, label }]
  const [variantsData, setVariantsData] = useState([]); // [{ variant_id, name, value, label }]
  const [searchText, setSearchText] = useState("");
  const [toolTipText, setToolTipText] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [showVariants, setShowVariants] = useState(false); // controls whether dropdown shows variants or projects
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [filteredVariants, setFilteredVariants] = useState([]);
  const containerRef = useRef(null);
  const autoSelectRef = useRef({ projectId: null });

  // Fetch projects (initial)
  const getProjects = async () => {
    const apiDetails = projectConfigAPI?.get_projects_main;
    await dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: false,
        params: {
          page_number: 1,
          page_size: 10000,
          order_by_column: "created_at", //@Changed by abdul
          order_by_sort: "DESC",
        },
      }),
    );
  };

  // Fetch variants for selected project (selectedProjectId is from redux)
  const getVariants = async (projectId) => {
    if (!projectId) {
      setVariantsData([]);
      return;
    }

    const apiDetails = projectConfigAPI.get_variants_main;
    let resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: false,
        params: {
          project_id: Number(projectId),
          is_complete_details_required: true,
          sort_column: "name",
          sort_order: "ASC",
        },
      }),
    );
    return resp.data;
    // Note: variants will come into redux and be handled by the variantsFromRedux useEffect below
  };

  const truncate = (str, max = 8) => {
    if (!str) return "";
    return str.length > max ? str.substring(0, max) + "..." : str;
  };

  // initial load
  useEffect(() => {
    getProjects();
  }, []);

  // When projects redux changes -> map to UI-friendly structure
  useEffect(() => {
    const mapped = (projects || []).map((p) => ({
      project_id: p.project_id,
      name: p.name,
      value: p.project_id,
      label: p.name,
    }));
    setProjectsData(mapped);
    setFilteredProjects(mapped);
  }, [projects]);

  useEffect(() => {
    if (!selectedProjectId || !showVariants) return;

    // Filter variants belonging to the currently selected project
    const currentProjectVariants = (variantsFromRedux || []).filter(
      (v) => Number(v.project_id) === Number(selectedProjectId),
    );

    const mapped = currentProjectVariants.map((v) => ({
      variant_id: v.variant_id,
      name: v.name,
      value: v.variant_id,
      label: v.name,
      project_id: v.project_id,
    }));
    setVariantsData(mapped);
    setFilteredVariants(mapped);
  }, [variantsFromRedux, selectedProjectId, showVariants]);

  // When selectedProjectId changes (redux), fetch its variants
  useEffect(() => {
    if (selectedProjectId) {
      getVariants(selectedProjectId);
    } else {
      setVariantsData([]);
      setFilteredVariants([]);
    }
  }, [selectedProjectId]);

  // Auto-select first variant once when variants arrive for the current project
  useEffect(() => {
    if (!selectedProjectId) return;
    const currentProjectVariants = (variantsFromRedux || []).filter(
      (v) => Number(v.project_id) === Number(selectedProjectId),
    );
    if (currentProjectVariants.length === 0) return;

    // Only auto-select if no variant is currently selected OR this is a re-selection of same project
    if (
      (autoSelectRef.current.projectId !== selectedProjectId ||
        autoSelectRef.current.projectId === null) &&
      !selectedVariantDetails
    ) {
      const first = currentProjectVariants[0];

      dispatch(selectVariantAction(first.variant_id));
      const projectName =
        selectedProjectDetails?.name ||
        projectsData.find(
          (p) => Number(p.project_id) === Number(selectedProjectId),
        )?.name ||
        "";
      setSearchText(
        projectName
          ? `${truncate(projectName)} [${truncate(first.name)}]`
          : `[${truncate(first.name)}]`,
      );
      setToolTipText(
        projectName ? `${projectName} [${first.name}]` : first.name,
      );
      autoSelectRef.current.projectId = selectedProjectId;
    }
  }, [
    variantsFromRedux,
    selectedProjectId,
    dispatch,
    selectedProjectDetails,
    projectsData,
  ]);

  // Initialize searchText from redux-selected values on mount & when they change
  useEffect(() => {
    if (selectedProjectDetails?.name) {
      const projectName = selectedProjectDetails.name;
      const variantName = selectedVariantDetails?.name;
      setSearchText(
        variantName
          ? `${truncate(projectName)} [${truncate(variantName)}]`
          : truncate(projectName),
      );
      setToolTipText(
        variantName ? `${projectName} [${variantName}]` : projectName,
      );
    } else {
      setSearchText("");
      setToolTipText("");
    }
  }, [selectedProjectDetails, selectedVariantDetails]);

  // Click outside logic: collapse and restore searchText to selected values
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setExpanded(false);

        // Always restore project + variant text
        if (selectedProjectDetails) {
          const projectName = selectedProjectDetails.name || "";
          const variantName = selectedVariantDetails?.name;
          setSearchText(
            variantName
              ? `${truncate(projectName)} [${truncate(variantName)}]`
              : truncate(projectName),
          );
          setToolTipText(
            variantName ? `${projectName} [${variantName}]` : projectName,
          );
        }

        // Restore mode -> showVariants should match project selection
        setShowVariants(!!selectedVariantDetails);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedProjectDetails, selectedVariantDetails]);

  useEffect(() => {
    const text = (searchText || "").trim();
    console.log("---selected project---", selectedProjectDetails);
    // parse "Project [Variant]" into parts
    const match = text.match(/^([^\[]*)(?:\[(.*?)\])?$/);
    const projectPart = (match?.[1] || "").trim().toLowerCase();
    const variantPart = (match?.[2] || "").trim().toLowerCase();
    const hasBracket = text.includes("[");

    // helper to filter projects by substring
    const filterProjectsBy = (q) => {
      if (!q) return projectsData;
      const filtered = projectsData.filter((p) =>
        p.label.toLowerCase().includes(q),
      );
      console.log("filtered project", filtered);
      return filtered.length
        ? filtered
        : [
            {
              label: "Project not found",
              name: "Project not found",
              disabled: true,
            },
          ];
    };

    // find exact matching project by label (exact equality)
    const matchedProjectExact = projectsData.find(
      (p) =>
        p.label.toLowerCase() === selectedProjectDetails?.name?.toLowerCase(),
    );

    console.log("---matchedProjectExact---", matchedProjectExact);

    // --------- Handle backspace inside brackets ---------
    // If the user deletes the ']' or empties variantPart, reset variant search
    if (hasBracket && variantPart.length === 0 && text.endsWith("[")) {
      setShowVariants(false);
      setFilteredProjects(filterProjectsBy(projectPart));
      return;
    }

    // ---- Decision tree ----
    // 1) Bracket syntax present -> "project [variant]" editing

    if (hasBracket) {
      if (matchedProjectExact && variantPart.length > 0) {
        setShowVariants(true);
        const baseVariants = variantsData.filter((v) =>
          v.project_id
            ? Number(v.project_id) === Number(matchedProjectExact.value)
            : true,
        );
        const filtered = baseVariants.filter((v) =>
          v.label.toLowerCase().includes(variantPart),
        );
        setFilteredVariants(filtered.length ? filtered : baseVariants);
        return;
      }

      // fallback to project search if project doesn't match exactly OR variantPart is empty
      setShowVariants(false);
      setFilteredProjects(filterProjectsBy(projectPart));
      return;
    }

    // 2) No bracket present -> project search mode
    if (!text) {
      setShowVariants(false);
      setFilteredProjects(projectsData);
      return;
    }

    // 3) Partial or exact project typed (no bracket) -> filter projects
    setShowVariants(false);
    setFilteredProjects(filterProjectsBy(projectPart));
  }, [searchText, projectsData, variantsData]);

  const handleMouseDown = () => {
    setExpanded(true);
    const input = inputRef.current;
    const text = searchText.trim();
    if (!input) return;
    const hasVariant = text.includes("[") && text.includes("]");

    if (hasVariant) {
      setTimeout(() => {
        input.setSelectionRange(text.length, text.length);
      }, 0);
    }
  };

  const handleFocus = () => {
    setExpanded(true);
    const input = inputRef.current;
    const text = searchText.trim();

    if (!input) return;
    const hasVariant = text.includes("[") && text.includes("]");

    // If variant part exists -> set cursor at end

    if (hasVariant) {
      setTimeout(() => {
        input.setSelectionRange(text.length, text.length);
      }, 0);
    }
    // CASE 3 → empty input → show all projects
    if (!text) {
      setShowVariants(false);
      setFilteredProjects(projectsData);
      return;
    }

    // Extract parts
    const match = text.match(/^([^\[]*)(?:\[(.*?)\])?$/);
    const projectPart = match?.[1]?.trim()?.toLowerCase() || "";
    const variantPart = match?.[2]?.trim()?.toLowerCase() || "";

    console.log("projectPart", projectPart, "variantPart", variantPart);

    // Detect if project part matches a real project
    const matchedProject = projectsData.find(
      (p) =>
        p.label.toLowerCase() === selectedProjectDetails?.name?.toLowerCase(),
    );

    // Detect valid variant mode: must have BOTH a matching project & a complete variant part
    const hasValidVariant = matchedProject && variantPart.length > 0;

    // CASE 1 → valid "Project [Variant]" → show variants
    if (hasValidVariant) {
      setShowVariants(true);
      setFilteredVariants(variantsData);
      return;
    }

    // CASE 2 → text contains ONLY project part → list projects matching it
    const filtered = projectsData.filter((p) =>
      p.label.toLowerCase().includes(projectPart),
    );

    setShowVariants(false);
    setFilteredProjects(filtered.length ? filtered : projectsData);
  };

  const handleKeyDown = (e) => {
    if (e.key !== "Backspace") return;

    const input = inputRef.current;
    if (!input) return;

    const cursorPos = input.selectionStart;
    const value = searchText;

    const startBracket = value.indexOf("[");
    const endBracket = value.indexOf("]");

    // Condition: cursor must be EXACTLY at the end (after "]")
    const isCursorAtVariantEnd = cursorPos === endBracket + 1;

    const variantExists = startBracket !== -1 && endBracket !== -1;

    if (variantExists && isCursorAtVariantEnd) {
      e.preventDefault(); // stop default backspace behavior

      // Remove full "[...]" including spaces
      // const newValue = value.slice(0, startBracket).trimEnd();
      const newValue = selectedProjectDetails?.name;

      setSearchText(newValue);
      setToolTipText("");
      setShowVariants(false);

      // Focus after setting state
      setTimeout(() => {
        input.setSelectionRange(newValue.length, newValue.length);
      }, 0);

      return; // stop here
    }

    // Otherwise normal backspace (your filter logic will run)
  };

  const handleClear = () => {
    setSearchText("");
    setToolTipText("");
    setExpanded(true);
    setShowVariants(false); // show projects only on clear
    setFilteredProjects(projectsData || []);
  };

  const handleProjectClick = async (project) => {
    if (!project) return;

    // Reset auto-select ref to allow variant loading for same project
    autoSelectRef.current.projectId = null;

    // If clicking the same project, force variant loading
    if (Number(selectedProjectId) === Number(project.value)) {
      // Force fetch variants and trigger auto-selection
      await getVariants(selectedProjectId);

      // Force update variants data for same project
      const currentProjectVariants = (variantsFromRedux || []).filter(
        (v) => Number(v.project_id) === Number(selectedProjectId),
      );
      const mapped = currentProjectVariants.map((v) => ({
        variant_id: v.variant_id,
        name: v.name,
        value: v.variant_id,
        label: v.name,
        project_id: v.project_id,
      }));
      setVariantsData(mapped);
      setFilteredVariants(mapped);
      setShowVariants(true);

      // Auto-select first variant
      if (mapped.length > 0) {
        const first = mapped[0];
        dispatch(selectVariantAction(first.variant_id));
        setSearchText(`${truncate(project.label)} [${truncate(first.name)}]`);
        setToolTipText(`${project.label} [${first.name}]`);
      }
    } else {
      // Select different project; variants will be fetched by useEffect on selectedProjectId
      dispatch(selectProjectAction(Number(project.value)));
    }

    setSearchText(project.label);
    setToolTipText(project.label);
    setExpanded(false);
  };

  const handleVariantClick = (variant) => {
    console.log("---handleVariantClick---", variant);
    if (!variant) return;

    dispatch(selectVariantAction(variant.variant_id));
    // set input immediately to selected value
    // Use selectedProjectDetails.name from redux for the project label; if missing fallback to searchText's project part
    const projectLabel =
      selectedProjectDetails?.name || searchText.split("[")[0]?.trim() || "";
    setSearchText(`${truncate(projectLabel)} [${truncate(variant.name)}]`);
    setToolTipText(`${projectLabel} [${variant.name}]`);
    setExpanded(false);
  };

  // Render
  return (
    <Box
      ref={containerRef}
      sx={{
        width: 300,
        fontFamily: "Arial",
        position: "relative",
        borderRadius: 0,
      }}
    >
      <Tooltip
        title={toolTipText}
        arrow
        placement="right"
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "#000", // pure black tooltip
              color: "#fff",
              borderRadius: 0,
              boxShadow: 3,
              p: 1,
              "& .MuiTooltip-arrow": {
                color: "#000",
              },
            },
          },
        }}
      >
        <TextField
          fullWidth
          size="small"
          value={searchText}
          inputRef={inputRef}
          onKeyDown={handleKeyDown}
          placeholder="Search project or variant..."
          onChange={(e) => {
            const text = e.target.value;
            if (text?.includes("]") || text?.includes("[")) {
              return;
            }
            setSearchText(text);
            setToolTipText("");
          }}
          onFocus={handleFocus}
          onMouseDown={handleMouseDown}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "black",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {searchText && (
                  <IconButton size="small" onClick={handleClear}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Tooltip>

      {expanded && (
        <Paper
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            maxHeight: 250,
            overflowY: "auto",
            border: "1px solid #ccc",
            mt: 0.5,
            zIndex: 10,
          }}
        >
          <List disablePadding>
            {/* Projects list */}
            {!showVariants &&
              (filteredProjects || []).map((project) => (
                <ListItemButton
                  key={project.value}
                  onClick={() => {
                    !project.disabled && handleProjectClick(project);
                  }}
                  sx={{
                    bgcolor:
                      selectedProjectDetails?.project_id === project.project_id
                        ? "#000"
                        : "#fff",
                    color:
                      selectedProjectDetails?.project_id === project.project_id
                        ? "#fff"
                        : !project.disabled
                          ? "#000"
                          : "#363535ff",
                    "&:hover": {
                      bgcolor: !project.disabled ? "#000" : "#fff",
                      color: !project.disabled ? "#fff" : "#000",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <ListItemText
                    primary={project.label}
                    primaryTypographyProps={{
                      textTransform: "capitalize",
                    }}
                  />
                </ListItemButton>
              ))}

            {/* Variants list for the currently selected project */}
            {showVariants &&
              (filteredVariants || []).map((variant) => (
                <ListItemButton
                  key={variant.value}
                  onClick={() => handleVariantClick(variant)}
                  sx={{
                    bgcolor:
                      selectedVariantDetails?.variant_id === variant.variant_id
                        ? "#000"
                        : "#fff",
                    color:
                      selectedVariantDetails?.variant_id === variant.variant_id
                        ? "#fff"
                        : "#000",
                    "&:hover": { bgcolor: "#000", color: "#fff" },
                    transition: "all 0.2s ease",
                    pl: 3,
                  }}
                >
                  <ListItemText
                    primary={variant.label}
                    primaryTypographyProps={{
                      textTransform: "capitalize",
                    }}
                  />
                </ListItemButton>
              ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}

export default memo(ProjectVariantSearchbar);
